import React, { MouseEventHandler } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty, isNull } from 'lodash';
import { TextField } from '@material-ui/core';
import { toast } from 'react-toastify';
import SahamDialog from 'components/SahamDialog';
import { translate } from 'containers/services/translate';
import {
  ACTION,
  CORRECTIONAL_JUDICIARY_PROCESS_ID,
  DEFAULT_LINK_AUXILIARE,
  DEFAULT_LINK_PROVIDER,
  DEFAULT_LINK_BENEFICIARY_CODE,
  PROCEDURE_STEPS_VALUES,
  FIRM_REFERENCE,
} from 'containers/LawyerFeeNote/constants';
import messages from 'containers/LawyerFeeNote/messages';
import { FEE_NOTE, PROCEDURE_STATUSES } from 'containers/data-structure';
import {
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import { extractIntervenantType } from 'containers/RecapPage/utils';
import {
  formatJudgementDate,
  getPaymentForCosts,
  getPaymentInfoFeeNote,
} from 'containers/LawyerFeeNote/utils';
import {
  compareDate,
  returnDefaultCodeByType,
} from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import {
  DEFAULT_INTERVENANT_CODE,
  DEFAULT_PROVIDER_CODE,
  HOLDER_PV_LAWYER,
  HOLDER_PV_PROVIDER,
  PROFIL_OPENNER_GPO,
} from 'containers/RecapPage/canstants';
import { isDesactivateIntervenant } from 'services/utils';
import moment from 'moment';
import WarningCard from 'components/InvoiceFeeNoteDialog/WarningCard';

const fullSize = { width: '100%' };
export interface FeeNoteActionsDialogProps {
  intl: IntlShape;
  open: boolean;
  action: string;
  handleClose: Function;
  beneficiary: string;
  id: number;
  makeDecisionFeeNote: Function;
  requestFeeNotes: Function;
  claimNumber: string;
  claimId: number | null;
  page: number;
  pageSize: number;
  feeNotes: any;
  listings: Listings;
  allFeeNotes?: any;
  viewByClaim: boolean;
  linkToBeneficiaryCodes?: SagisConfig[];
  setNotificationFeeNote?: Function;
  requestUpdateFirmReferenceManager: Function;
  limitValidationInvoiceDate: any;
}

const FeeNoteActionsDialog: React.FC<FeeNoteActionsDialogProps> = (
  props,
): JSX.Element => {
  const [reason, setReason] = React.useState<string>('');
  const [firmReference, setFirmReference] = React.useState<string>('');
  const currentFeeNote = props.feeNotes.find(
    (el: { id: number }) => el.id === Number(props.id),
  );

  const isJudiciaryProcessDefined = !isEmpty(
    get(currentFeeNote, 'judiciaryProcess'),
  );

  const isJudiciaryProcessCorrectional =
    get(currentFeeNote, 'judiciaryProcess.procedureNatureId') ===
    CORRECTIONAL_JUDICIARY_PROCESS_ID;

  const judiciaryProcessStatus = get(currentFeeNote, 'judiciaryProcess.status');

  const disableFeeNoteValidation =
    isJudiciaryProcessDefined &&
    !isJudiciaryProcessCorrectional &&
    judiciaryProcessStatus !== PROCEDURE_STATUSES.VALIDATED;

  const handleGetTitleDialog = () => {
    if (props.action === ACTION.VALIDATE)
      return translate({ ...messages.validateActionDialogTitle }, props);
    else if (props.action === ACTION.REJECT)
      return translate({ ...messages.rejectActionDialogTitle }, props);
    else return translate({ ...messages.dialogFirmReferenceTitle }, props);
  };

  const handleChange = (
    event: React.ChangeEvent<{
      value: any;
      name: any;
    }>,
  ) => {
    if (event.target.name === FIRM_REFERENCE) {
      setFirmReference(event.target.value);
    } else {
      setReason(event.target.value);
    }
  };

  /* istanbul ignore next */
  const handleValidateFeeNote = (id: number) => {
    const selectFeeNote = props.feeNotes.find(
      (feeNote: { id: number }) => feeNote.id === Number(id),
    );
    if (
      props.action === ACTION.ADD_FIRM_REFERENCE &&
      props.requestUpdateFirmReferenceManager
    ) {
      props.requestUpdateFirmReferenceManager(
        {
          id,
          firmReference,
          claimNumber: props.claimNumber,
          viewByClaim: props.viewByClaim,
          claimId: props.claimId,
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
        },
        successCallBackValidate,
      );
    } else {
      /* istanbul ignore next */
      const codeTown = isDesactivateIntervenant(
        get(props.listings, 'intervenants', []),
        selectFeeNote.codeIntervenant,
      )
        ? get(props.listings, 'inactiveIntervenants', []).find(
            (element: any) => element.ItemKey === selectFeeNote.codeIntervenant,
          )
        : get(props.listings, 'intervenants', []).find(
            element => element.ItemKey === selectFeeNote.codeIntervenant,
          );

      const validatedDecision = {
        status: STATUS_VALIDATED,
        id: Number(id),
        feeNote: {
          ...selectFeeNote,
          invoiceNumber: get(selectFeeNote, 'invoiceFeeNote.invoiceNumber'),
          InvoiceDate: get(selectFeeNote, 'invoiceFeeNote.invoiceDate'),
          InvoiceReceptionDate: get(
            selectFeeNote,
            'invoiceFeeNote.receptionDate',
          ),
          contractorCode: selectFeeNote.codeIntervenant,
          claimExtCode: selectFeeNote.claim.claimNumber,
          contractorTypeCode: !isEmpty(selectFeeNote.feeNoteMission)
            ? extractIntervenantType(
                props.listings,
                returnDefaultCodeByType(
                  get(selectFeeNote.feeNoteMission, 'mission.type'),
                ),
              )
            : extractIntervenantType(
                props.listings,
                get(selectFeeNote.claim, 'intervenantTypeCode'),
              ) === DEFAULT_INTERVENANT_CODE
            ? DEFAULT_INTERVENANT_CODE
            : get(selectFeeNote, 'step') === 'PV'
            ? get(selectFeeNote.claim, 'profilOpenner', null) ===
              PROFIL_OPENNER_GPO
              ? get(selectFeeNote.claim, 'pvHolder.pvHolderTypeId', null) ===
                HOLDER_PV_LAWYER
                ? DEFAULT_INTERVENANT_CODE
                : DEFAULT_PROVIDER_CODE
              : extractIntervenantType(
                  props.listings,
                  get(selectFeeNote.claim, 'intervenantTypeCode'),
                )
            : DEFAULT_INTERVENANT_CODE,
          Amount: isNull(get(selectFeeNote, 'costs', ''))
            ? selectFeeNote.amountTtc
            : selectFeeNote.costs,
          beneficiaryReference: !isEmpty(selectFeeNote.feeNoteMission)
            ? get(selectFeeNote.feeNoteMission, 'mission.reference')
            : `${get(selectFeeNote, 'firmReference', '')} - ${get(
                selectFeeNote,
                'judiciaryProcess.courtNumber',
                '',
              )} - ${PROCEDURE_STEPS_VALUES[selectFeeNote.step]}`,
          updateDate: new Date(),
          townCode: get(codeTown, 'townCode', ''),
          address: get(codeTown, 'address', ''),
          linkToBeneficiaryCode: !isEmpty(selectFeeNote.feeNoteMission)
            ? DEFAULT_LINK_AUXILIARE
            : get(selectFeeNote.claim, 'profilOpenner') === PROFIL_OPENNER_GPO
            ? get(selectFeeNote.claim, 'pvHolder.pvHolderTypeId', null) ===
              HOLDER_PV_PROVIDER
              ? DEFAULT_LINK_PROVIDER
              : get(props, 'linkToBeneficiaryCodes', []).find(
                  (el: SagisConfig) =>
                    el.ItemKey === DEFAULT_LINK_BENEFICIARY_CODE,
                )
            : get(props, 'linkToBeneficiaryCodes', []).find(
                (el: SagisConfig) =>
                  el.ItemKey === DEFAULT_LINK_BENEFICIARY_CODE,
              ),
          judgementDate: formatJudgementDate(selectFeeNote),
          ...(!isNull(get(selectFeeNote, 'costs', ''))
            ? getPaymentForCosts(props.listings, selectFeeNote)
            : getPaymentInfoFeeNote(props.listings)),
          documentId: !isNull(get(selectFeeNote, 'costs', '')) ? 88 : 52,
        },

        username: currentUserStorage.get().username,
      };
      const rejectedDecision = {
        status: STATUS_REJECTED,
        comment: reason,
        id: Number(id),
        feeNote: {
          ...selectFeeNote,
          contractorCode: selectFeeNote.codeIntervenant,
          claimExtCode: selectFeeNote.claim.claimNumber,
          contractorTypeCode:
            extractIntervenantType(
              props.listings,
              get(selectFeeNote.claim, 'intervenantTypeCode'),
            ) === DEFAULT_INTERVENANT_CODE
              ? DEFAULT_INTERVENANT_CODE
              : get(selectFeeNote, 'step') === 'PV'
              ? get(selectFeeNote.claim, 'profilOpenner') === PROFIL_OPENNER_GPO
                ? get(selectFeeNote.claim, 'pvHolder.pvHolderTypeId', null) ===
                  HOLDER_PV_LAWYER
                  ? DEFAULT_INTERVENANT_CODE
                  : DEFAULT_PROVIDER_CODE
                : extractIntervenantType(
                    props.listings,
                    get(selectFeeNote.claim, 'intervenantTypeCode'),
                  )
              : DEFAULT_INTERVENANT_CODE,
          Amount: selectFeeNote.amountTtc,
          updateDate: new Date(),
          townCode: get(codeTown, 'townCode', ''),
          adresse: get(codeTown, 'address', ''),
        },

        username: currentUserStorage.get().username,
      };
      const decision =
        props.action === ACTION.VALIDATE ? validatedDecision : rejectedDecision;

      props.makeDecisionFeeNote(
        {
          ...decision,
          claimNumber: props.claimNumber,
          viewByClaim: props.viewByClaim,
          claimId: props.claimId,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          isStatusManagement: featureService.hasFeature(
            FEATURES.STATUS_MANAGEMENT,
          ),
        },
        successCallBackValidate,
      );
    }
    props.handleClose();
  };

  const handleToastMessage = (action: string) => {
    if (action === ACTION.VALIDATE)
      return toast.success(
        translate(messages.validationSuccess, props, {
          lawyer: props.beneficiary,
        }),
      );
    else if (action === ACTION.REJECT)
      return toast.success(
        translate(messages.rejectSuccess, props, {
          lawyer: props.beneficiary,
        }),
      );
    else if (action === ACTION.ADD_FIRM_REFERENCE)
      return toast.success(
        translate(messages.managerFirmReferenceDialogSuccessMessage, props),
      );
    else return null;
  };

  const successCallBackValidate = () => {
    handleToastMessage(props.action);
    props.setNotificationFeeNote && props.setNotificationFeeNote(null);
    if (props.viewByClaim) {
      props.requestFeeNotes(props.claimNumber);
    } else {
      props.requestFeeNotes(STATUS_PENDING, props.page, props.pageSize);
    }
  };
  const dialogAction = (action: string) => {
    if (action === ACTION.VALIDATE && !disableFeeNoteValidation)
      return (
        <>
          {compareDate(
            moment().format('YYYY-MM-DD'),
            moment(
              get(currentFeeNote, 'invoiceFeeNote.invoiceDate', null),
            ).format('YYYY-MM-DD'),
          ) >= props.limitValidationInvoiceDate &&
            featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) && (
              <WarningCard
                message={translate(
                  messages.warningValidationInvoiceFeeNote,
                  props,
                  {
                    limitValidationDaysManager:
                      props.limitValidationInvoiceDate,
                  },
                )}
              />
            )}
          <div>{translate({ ...messages.confirmationSentence }, props)}</div>
        </>
      );
    else if (disableFeeNoteValidation && action === ACTION.VALIDATE) {
      return (
        <>
          {compareDate(
            moment().format('YYYY-MM-DD'),
            moment(
              get(currentFeeNote, 'invoiceFeeNote.invoiceDate', null),
            ).format('YYYY-MM-DD'),
          ) >= props.limitValidationInvoiceDate &&
            featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) && (
              <WarningCard
                message={translate(
                  messages.warningValidationInvoiceFeeNote,
                  props,
                  {
                    limitValidationDaysManager:
                      props.limitValidationInvoiceDate,
                  },
                )}
              />
            )}
          <div>
            {translate({ ...messages.feeNoteDisableValidation }, props)}
          </div>
        </>
      );
    } else if (props.action === ACTION.ADD_FIRM_REFERENCE) {
      return (
        <div>
          <TextField
            label={translate({ ...messages.firmReference }, props)}
            className="input"
            style={fullSize}
            name={FIRM_REFERENCE}
            value={firmReference}
            onChange={handleChange}
          ></TextField>
        </div>
      );
    } else if (props.action === ACTION.REJECT) {
      return (
        <div>
          <TextField
            label={translate({ ...messages.motifLabel }, props)}
            className="input"
            style={fullSize}
            name={FEE_NOTE.REJECTION_REASON}
            value={reason}
            onChange={handleChange}
          ></TextField>
        </div>
      );
    }
  };

  return (
    <SahamDialog
      handleValidate={() => handleValidateFeeNote(props.id)}
      handleClose={props.handleClose as MouseEventHandler}
      title={handleGetTitleDialog()}
      cancelTitle={translate({ ...messages.cancelBtn }, props)}
      confirmTitle={translate({ ...messages.confirmBtn }, props)}
      open={props.open}
      validateButtonDisabled={
        disableFeeNoteValidation && props.action === ACTION.VALIDATE
      }
    >
      {dialogAction(props.action)}
    </SahamDialog>
  );
};

export default injectIntl(FeeNoteActionsDialog);
